import React, { useState, useEffect, useRef, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import { flushSync } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MessageSquare,
  Plus,
  Send,
  Paperclip,
  File,
  X,
  Trash2,
  Download,
  Loader,
  Sparkles,
  ExternalLink,
  Edit2,
  Check,
  X as XIcon,
  Eye,
  FileText,
  Upload,
  FolderSync,
  Globe,
  BookOpen,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import Layout from '@/components/Layout';
import StreamingContent from '@/components/StreamingContent';
import BulkUploadModal from '@/components/BulkUploadModal';
import SharePointModal from '@/components/SharePointModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Chat = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  // Use useReducer for messages to force updates
  const [messages, messagesDispatch] = useReducer((state, action) => {
    switch (action.type) {
      case 'SET':
        return action.payload;
      case 'ADD':
        return [...state, action.payload];
      case 'UPDATE':
        return state.map(m => 
          m.id === action.id ? { ...m, ...action.updates } : m
        );
      case 'REMOVE':
        return state.filter(m => !action.ids.includes(m.id));
      default:
        return state;
    }
  }, []);
  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [currentWorkspace, setCurrentWorkspace] = useState('general');
  const [expandedSources, setExpandedSources] = useState({});
  const [sourcesPanel, setSourcesPanel] = useState({ open: false, sources: [], title: '' });
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [filePreviewOpen, setFilePreviewOpen] = useState(false);
  const [filePreviewData, setFilePreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [sharePointOpen, setSharePointOpen] = useState(false);
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  const workspaces = [
    { id: 'general', name: 'General Chat', icon: '💬' },
    { id: 'research', name: 'Research', icon: '🔍' },
    { id: 'analysis', name: 'Analysis', icon: '📊' },
  ];

  useEffect(() => {
    if (!user) {
      navigate('/login');
    } else {
      fetchSessions();
    }
  }, [user, navigate]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 150) + 'px';
    }
  }, [inputMessage]);

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/chat/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // API returns array directly, not wrapped in object
      const sessionsData = Array.isArray(response.data) ? response.data : (response.data.sessions || []);
      setSessions(sessionsData);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      if (error.response?.status !== 403) {
        // Don't show error for non-RM users
        toast.error('Failed to load chat history');
      }
    }
  };

  const loadSession = async (sessionId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Use single endpoint that returns session, messages, and files
      const response = await axios.get(`${API}/chat/sessions/${sessionId}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setCurrentSession(response.data.session);
      messagesDispatch({ type: 'SET', payload: response.data.messages || [] });
      setFiles(response.data.files || []);
    } catch (error) {
      console.error('Error loading session:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Failed to load chat session');
    } finally {
      setLoading(false);
    }
  };

  const createNewSession = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/chat/sessions`,
        { title: 'New Chat' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchSessions();
      // Backend returns session directly, not wrapped
      const session = response.data.session || response.data;
      setCurrentSession(session);
      messagesDispatch({ type: 'SET', payload: [] });
      setFiles([]);
      toast.success('New chat created');
    } catch (error) {
      console.error('Error creating session:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Failed to create new chat');
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !currentSession || sending) return;

    const userMessage = inputMessage;
    setInputMessage('');
    setSending(true);

    const tempUserMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString(),
    };
    messagesDispatch({ type: 'ADD', payload: tempUserMessage });

    // Create temporary AI message for streaming
    const tempAiMessageId = `ai-${Date.now()}`;
    const tempAiMessage = {
      id: tempAiMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      streaming: true
    };
    messagesDispatch({ type: 'ADD', payload: tempAiMessage });

    try {
      const response = await fetch(
        `${API}/chat/sessions/${currentSession.id}/messages/stream`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message: userMessage,
            session_id: currentSession.id
          })
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      
      let streamingContent = '';
      let sources = [];
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;

        const text = decoder.decode(value, { stream: true });
        buffer += text;

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6).trim();
            if (!jsonStr) continue;
            
            try {
              const data = JSON.parse(jsonStr);
              
              if (data.type === 'start') {
                messagesDispatch({ 
                  type: 'UPDATE', 
                  id: tempUserMessage.id, 
                  updates: data.user_message 
                });
              } else if (data.type === 'tool_call') {
                // Show tool-call indicator as streaming content
                const toolMsg = `*${data.message}*\n\n`;
                flushSync(() => {
                  messagesDispatch({
                    type: 'UPDATE',
                    id: tempAiMessageId,
                    updates: {
                      content: toolMsg,
                      streaming: true,
                      toolCall: true,
                      lastUpdate: Date.now()
                    }
                  });
                });
              } else if (data.type === 'chunk') {
                streamingContent += data.content;
                flushSync(() => {
                  messagesDispatch({
                    type: 'UPDATE',
                    id: tempAiMessageId,
                    updates: {
                      content: streamingContent,
                      streaming: true,
                      toolCall: false,
                      lastUpdate: Date.now() // Force new reference
                    }
                  });
                });
                
              } else if (data.type === 'sources') {
                sources = data.sources || [];
                console.log('[SSE] Received sources:', JSON.stringify(sources));
              } else if (data.type === 'done') {
                console.log('[SSE] Done event, attaching sources:', JSON.stringify(sources));
                // Update final message
                messagesDispatch({ 
                  type: 'UPDATE', 
                  id: tempAiMessageId, 
                  updates: { 
                    ...data.ai_message, 
                    content: streamingContent || data.ai_message?.content || '',
                    streaming: false, 
                    sources: sources 
                  }
                });
                
                // Refresh session
                fetchSessions();
                const sessionResponse = await axios.get(
                  `${API}/chat/sessions/${currentSession.id}`,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                setCurrentSession(sessionResponse.data.session);
                break;
              } else if (data.type === 'error') {
                throw new Error(data.error);
              }
            } catch (parseError) {
              // Skip malformed JSON
            }
          }
        }
      }

      // Safety fallback: if stream ended but 'done' event was never processed,
      // force streaming off so markdown renders and blinker stops
      if (streamingContent) {
        messagesDispatch({
          type: 'UPDATE',
          id: tempAiMessageId,
          updates: {
            content: streamingContent,
            streaming: false,
            sources: sources.length > 0 ? sources : undefined,
            lastUpdate: Date.now()
          }
        });
      }

      setSending(false);

    } catch (error) {
      console.error('Streaming error:', error);
      toast.error('Failed to send message');
      // Ensure streaming state is cleared on error too
      messagesDispatch({
        type: 'UPDATE',
        id: tempAiMessageId,
        updates: { streaming: false }
      });
      if (!streamingContent) {
        messagesDispatch({
          type: 'REMOVE',
          ids: [tempUserMessage.id, tempAiMessageId]
        });
      }
      setSending(false);
    }
  };

  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this chat?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/chat/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchSessions();
      if (currentSession?.id === sessionId) {
        setCurrentSession(null);
        messagesDispatch({ type: 'SET', payload: [] });
        setFiles([]);
      }
      toast.success('Chat deleted');
    } catch (error) {
      console.error('Error deleting session:', error);
      toast.error('Failed to delete chat');
    }
  };

  const updateSessionTitle = async (sessionId, newTitle) => {
    if (!newTitle.trim()) {
      toast.error('Title cannot be empty');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/chat/sessions/${sessionId}/title`, {
        title: newTitle.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update sessions list
      setSessions(prev => prev.map(session => 
        session.id === sessionId ? { ...session, title: newTitle.trim() } : session
      ));
      
      // Update current session if it's the one being edited
      if (currentSession?.id === sessionId) {
        setCurrentSession(prev => ({ ...prev, title: newTitle.trim() }));
      }
      
      setEditingSessionId(null);
      setEditingTitle('');
      toast.success('Chat title updated');
    } catch (error) {
      console.error('Error updating session title:', error);
      toast.error('Failed to update chat title');
    }
  };

  const startEditingTitle = (sessionId, currentTitle, e) => {
    e.stopPropagation();
    setEditingSessionId(sessionId);
    setEditingTitle(currentTitle);
  };

  const cancelEditingTitle = (e) => {
    e.stopPropagation();
    setEditingSessionId(null);
    setEditingTitle('');
  };

  const saveTitle = (sessionId, e) => {
    e.stopPropagation();
    updateSessionTitle(sessionId, editingTitle);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !currentSession) return;

    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/chat/sessions/${currentSession.id}/files`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setFiles(prev => [...prev, response.data.file]);
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  const deleteFile = async (fileId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/chat/sessions/${currentSession.id}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFiles(prev => prev.filter(f => f.id !== fileId));
      toast.success('File deleted');
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Failed to delete file');
    }
  };

  const exportChat = () => {
    if (!currentSession || messages.length === 0) return;

    const chatText = messages
      .map(m => `${m.role.toUpperCase()}: ${m.content}`)
      .join('\n\n');
    
    const blob = new Blob([chatText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-${currentSession.title}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleSources = (messageId) => {
    setExpandedSources(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  const openSourcesPanel = (sources) => {
    setSourcesPanel({ open: true, sources, title: `${sources.length} Source${sources.length !== 1 ? 's' : ''}` });
  };

  const closeSourcesPanel = () => {
    setSourcesPanel(prev => ({ ...prev, open: false }));
  };

  const getDomainFavicon = (url) => {
    try {
      const domain = new URL(url).hostname;
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
    } catch {
      return null;
    }
  };

  const openFilesSidebar = () => {
    setFilePreviewOpen(true);
  };

  const openFilePreview = async (fileId, filename) => {
    if (!currentSession) return;

    setLoadingPreview(true);
    setFilePreviewData(null);
    setPreviewDialogOpen(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/chat/sessions/${currentSession.id}/files/${fileId}/preview`,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
      );
      setFilePreviewData({ ...response.data, fileId });
    } catch (error) {
      console.error('Error loading file preview:', error);
      toast.error('Failed to load file preview');
      setPreviewDialogOpen(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  const openFilePreviewByName = async (filename) => {
    // Find file by exact match first, then partial match
    const normalizedSearch = filename.toLowerCase().trim();
    const file = files.find(f => f.filename === filename)
      || files.find(f => f.filename.toLowerCase().includes(normalizedSearch))
      || files.find(f => normalizedSearch.includes(f.filename.toLowerCase()));
    if (file) {
      openFilePreview(file.id, file.filename);
    } else {
      toast.error(`File "${filename}" not found`);
    }
  };

  // Parse message content and make source citations clickable
  const renderMessageWithSources = (content) => {
    if (!content) return null;
    
    // Pattern to match [Source: filename.ext]
    const sourcePattern = /\[Source:\s*([^\]]+)\]/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = sourcePattern.exec(content)) !== null) {
      // Add text before the source citation
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${lastIndex}`}>
            {content.slice(lastIndex, match.index)}
          </span>
        );
      }
      
      // Add clickable source citation
      const filename = match[1].trim();
      parts.push(
        <button
          key={`source-${match.index}`}
          onClick={() => openFilePreviewByName(filename)}
          className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#fffdf0] hover:bg-[#fffdf0] text-ugmonk-dark rounded text-sm font-medium transition-colors cursor-pointer mx-1"
          title={`Click to preview ${filename}`}
        >
          <FileText className="w-3 h-3" />
          {filename}
        </button>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < content.length) {
      parts.push(
        <span key={`text-${lastIndex}`}>
          {content.slice(lastIndex)}
        </span>
      );
    }
    
    return parts.length > 0 ? parts : content;
  };

  // PPT functions removed - now in WorkflowPage.js

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="h-[calc(100vh-4rem)]">
        <div className="max-w-[1600px] mx-auto h-full flex flex-col p-6">
          {/* Header */}
          <div className="mb-6 flex-shrink-0">
            <div className="flex items-center justify-between p-5 bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#ffd87a] to-[#ffd87a] rounded-lg flex items-center justify-center shadow-md">
                  <MessageSquare className="w-6 h-6 text-black" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900" style={{fontFamily: "'PerfectNineties', serif"}}>
                    OlafAI
                  </h1>
                  <p className="text-sm text-gray-600 flex items-center gap-2">
                    {files.length > 0 ? (
                      <>
                        <span className="w-2 h-2 bg-[#ffd87a] rounded-full animate-pulse"></span>
                        <span>Document-based mode • {files.length} file{files.length !== 1 ? 's' : ''}</span>
                      </>
                    ) : (
                      <>
                        <span className="w-2 h-2 bg-[#ffd87a] rounded-full animate-pulse"></span>
                        <span>Web-powered mode</span>
                      </>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Select value={currentWorkspace} onValueChange={setCurrentWorkspace}>
                  <SelectTrigger className="w-52 bg-white border-gray-300 hover:bg-gray-50 transition-colors">
                    <SelectValue>
                      <span className="flex items-center gap-2">
                        <span className="text-lg">{workspaces.find(w => w.id === currentWorkspace)?.icon}</span>
                        <span className="font-medium text-gray-900">{workspaces.find(w => w.id === currentWorkspace)?.name}</span>
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {workspaces.map(workspace => (
                      <SelectItem key={workspace.id} value={workspace.id}>
                        <span className="flex items-center gap-2">
                          <span className="text-lg">{workspace.icon}</span>
                          <span>{workspace.name}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={createNewSession}
                  className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black font-semibold shadow-sm hover:shadow-md transition-all"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Chat
                </Button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-6 flex-1 min-h-0">
            {/* Chat Sessions Sidebar */}
            <div className="col-span-3 flex flex-col min-h-0">
              <Card className="border-gray-200 bg-white shadow-sm flex-1 flex flex-col rounded-lg overflow-hidden min-h-0">
                <CardHeader className="pb-3 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 flex-shrink-0">
                  <CardTitle className="text-base flex items-center gap-2 text-gray-900 font-semibold">
                    <MessageSquare className="w-4 h-4 text-ugmonk-dark" />
                    Chat History
                  </CardTitle>
                </CardHeader>
                <CardContent className="bg-white flex-1 overflow-y-auto p-4">
                  {sessions.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <MessageSquare className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                      <p className="text-sm font-medium text-gray-900">No chats yet</p>
                      <p className="text-xs mt-1 text-gray-600">Start a new conversation</p>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {sessions.map(session => {
                        const isToday = new Date(session.updated_at).toDateString() === new Date().toDateString();
                        const isYesterday = new Date(session.updated_at).toDateString() === new Date(Date.now() - 86400000).toDateString();
                        const timeStr = isToday ? 'Today' : isYesterday ? 'Yesterday' : new Date(session.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        
                        return (
                          <div
                            key={session.id}
                            onClick={() => loadSession(session.id)}
                            className={`group flex items-center justify-between px-3 py-2.5 rounded-lg cursor-pointer transition-all ${
                              currentSession?.id === session.id
                                ? 'bg-[#fffdf0] border border-[#ffd87a]'
                                : 'hover:bg-gray-50 border border-transparent hover:border-gray-200'
                            }`}
                          >
                            <div className="flex-1 min-w-0 flex items-start gap-2">
                              <MessageSquare className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                                currentSession?.id === session.id ? 'text-ugmonk-dark' : 'text-gray-400'
                              }`} />
                              <div className="flex-1 min-w-0">
                                {editingSessionId === session.id ? (
                                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                    <Input
                                      value={editingTitle}
                                      onChange={(e) => setEditingTitle(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          saveTitle(session.id, e);
                                        } else if (e.key === 'Escape') {
                                          cancelEditingTitle(e);
                                        }
                                      }}
                                      className="text-sm h-6 px-1 py-0 border-[#ffd87a] focus:border-[#ffd87a] focus:ring-[#ffd87a]"
                                      autoFocus
                                    />
                                    <button
                                      onClick={(e) => saveTitle(session.id, e)}
                                      className="p-0.5 hover:bg-green-50 rounded transition-all"
                                    >
                                      <Check className="w-3 h-3 text-green-600" />
                                    </button>
                                    <button
                                      onClick={cancelEditingTitle}
                                      className="p-0.5 hover:bg-red-50 rounded transition-all"
                                    >
                                      <XIcon className="w-3 h-3 text-red-600" />
                                    </button>
                                  </div>
                                ) : (
                                  <p className={`text-sm font-medium truncate leading-snug ${
                                    currentSession?.id === session.id ? 'text-ugmonk-dark' : 'text-gray-900'
                                  }`}>
                                    {session.title}
                                  </p>
                                )}
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {timeStr}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              {editingSessionId !== session.id && (
                                <button
                                  onClick={(e) => startEditingTitle(session.id, session.title, e)}
                                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-[#fffdf0] rounded transition-all flex-shrink-0"
                                >
                                  <Edit2 className="w-3.5 h-3.5 text-ugmonk-grey" />
                                </button>
                              )}
                              <button
                                onClick={(e) => deleteSession(session.id, e)}
                                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 rounded transition-all flex-shrink-0"
                              >
                                <Trash2 className="w-3.5 h-3.5 text-red-500" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Main Chat Area */}
            <div className="col-span-9 flex flex-col min-h-0">
              <Card className="border-gray-200 bg-white shadow-sm flex-1 flex flex-col rounded-lg overflow-hidden min-h-0">
                {!currentSession ? (
                  <CardContent className="flex-1 flex items-center justify-center bg-white">
                    <div className="text-center max-w-lg">
                      <div className="w-24 h-24 bg-gradient-to-br from-[#ffd87a] to-[#ffd87a] rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-lg">
                        <Sparkles className="w-12 h-12 text-white" />
                      </div>
                      <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{fontFamily: "'PerfectNineties', serif"}}>
                        Start a Conversation
                      </h2>
                      <p className="text-gray-600 mb-8 text-lg">
                        Select a chat from history or create a new one to begin your AI-powered discussion
                      </p>
                      <Button
                        onClick={createNewSession}
                        className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black font-semibold px-8 py-3 text-base shadow-sm hover:shadow-md transition-all"
                      >
                        <Plus className="w-5 h-5 mr-2" />
                        Start New Chat
                      </Button>
                    </div>
                  </CardContent>
                ) : (
                  <>
                    {/* Chat Header */}
                    <CardHeader className="pb-4 border-b border-gray-200 bg-white flex-shrink-0">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          {editingSessionId === currentSession.id ? (
                            <div className="flex items-center gap-2">
                              <Input
                                value={editingTitle}
                                onChange={(e) => setEditingTitle(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    saveTitle(currentSession.id, e);
                                  } else if (e.key === 'Escape') {
                                    cancelEditingTitle(e);
                                  }
                                }}
                                className="text-xl font-bold h-10 px-2 border-[#ffd87a] focus:border-[#ffd87a] focus:ring-[#ffd87a]"
                                autoFocus
                              />
                              <button
                                onClick={(e) => saveTitle(currentSession.id, e)}
                                className="p-1 hover:bg-green-50 rounded transition-all"
                              >
                                <Check className="w-4 h-4 text-green-600" />
                              </button>
                              <button
                                onClick={cancelEditingTitle}
                                className="p-1 hover:bg-red-50 rounded transition-all"
                              >
                                <XIcon className="w-4 h-4 text-red-600" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 group">
                              <CardTitle className="text-xl font-bold text-gray-900">{currentSession.title}</CardTitle>
                              <button
                                onClick={(e) => startEditingTitle(currentSession.id, currentSession.title, e)}
                                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-[#fffdf0] rounded transition-all"
                              >
                                <Edit2 className="w-4 h-4 text-ugmonk-grey" />
                              </button>
                            </div>
                          )}
                          {files.length > 0 && (
                            <button
                              onClick={openFilesSidebar}
                              className="mt-2 flex items-center gap-2 text-sm text-ugmonk-dark hover:text-[#f5cc6a] transition-colors"
                            >
                              <File className="w-4 h-4" />
                              <span className="font-medium">{files.length} file{files.length !== 1 ? 's' : ''} attached</span>
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => setSharePointOpen(true)}
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-2 text-gray-900 border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-colors"
                          >
                            <FolderSync className="w-4 h-4" />
                            SharePoint
                          </Button>
                          <Button
                            onClick={exportChat}
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-2 text-gray-900 border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-colors"
                          >
                            <Download className="w-4 h-4" />
                            Export
                          </Button>
                        </div>
                      </div>
                    </CardHeader>


                    {/* Messages */}
                    <CardContent className="flex-1 overflow-y-auto p-6 bg-gray-50 min-h-0">
                      {loading ? (
                        <div className="flex flex-col items-center justify-center h-full">
                          <Loader className="w-10 h-10 animate-spin text-ugmonk-dark mb-4" />
                          <p className="text-gray-600">Loading messages...</p>
                        </div>
                      ) : messages.length === 0 ? (
                        <div className="flex items-center justify-center h-full text-center">
                          <div className="max-w-md">
                            <div className="w-20 h-20 bg-gradient-to-br from-[#fffdf0] to-[#ffd87a]/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                              <MessageSquare className="w-10 h-10 text-ugmonk-dark" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Ready to Chat</h3>
                            <p className="text-gray-600">
                              {files.length > 0
                                ? 'Ask questions about your uploaded documents. I\'ll provide detailed answers based on the content.'
                                : 'Start typing to begin the conversation. I can help with web search, information, and more.'}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-6">
                          {messages.map(message => (
                            <div
                              key={`${message.id}-${message.streaming ? 'streaming' : 'final'}`}
                              className={`flex ${
                                message.role === 'user' ? 'justify-end' : 'justify-start'
                              }`}
                            >
                              <div
                                className={`max-w-[75%] ${
                                  message.role === 'user'
                                    ? 'bg-gradient-to-br from-[#ffd87a] to-[#ffd87a] text-black rounded-2xl rounded-tr-sm shadow-sm'
                                    : 'bg-white text-gray-900 rounded-2xl rounded-tl-sm shadow-sm border border-gray-200'
                                } px-6 py-4`}
                              >
                                {message.role === 'assistant' ? (
                                  <>
                                    <StreamingContent 
                                      key={message.lastUpdate || message.id}
                                      content={message.content} 
                                      isStreaming={message.streaming}
                                      messageId={message.id}
                                      onSourceClick={openFilePreviewByName}
                                    />
                                    {/* Sources Section */}
                                    {message.sources && message.sources.length > 0 && (
                                      <div className="mt-4 pt-4 border-t border-gray-200">
                                        <button
                                          onClick={() => toggleSources(message.id)}
                                          className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-ugmonk-dark transition-colors"
                                        >
                                          {expandedSources[message.id] ? (
                                            <ChevronUp className="w-4 h-4" />
                                          ) : (
                                            <ChevronDown className="w-4 h-4" />
                                          )}
                                          <span>{message.sources.length} Source{message.sources.length !== 1 ? 's' : ''}</span>
                                        </button>
                                        {expandedSources[message.id] && (
                                          <div className="mt-3 space-y-2">
                                            {message.sources.map((source, idx) => (
                                              source.type === 'file' ? (
                                                <button
                                                  key={idx}
                                                  onClick={() => openFilePreview(source.file_id, source.filename)}
                                                  className="w-full flex items-center gap-2 p-2.5 bg-[#fffdf0] rounded-lg hover:bg-[#fffdf0] transition-colors group text-left"
                                                >
                                                  <FileText className="w-4 h-4 text-ugmonk-dark flex-shrink-0" />
                                                  <span className="text-sm font-medium text-gray-900 truncate flex-1">{source.filename}</span>
                                                  <Eye className="w-4 h-4 text-gray-400 group-hover:text-ugmonk-dark flex-shrink-0" />
                                                </button>
                                              ) : source.type === 'web' ? (
                                                <a
                                                  key={idx}
                                                  href={source.url}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
                                                >
                                                  <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-ugmonk-dark flex-shrink-0" />
                                                  <span className="text-sm font-medium text-gray-900 truncate flex-1">{source.title || source.url}</span>
                                                </a>
                                              ) : (
                                                <div key={idx} className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg">
                                                  <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                  <span className="text-sm text-gray-700 truncate flex-1">{source.filename || source.title || 'Unknown source'}</span>
                                                </div>
                                              )
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div className="whitespace-pre-wrap leading-relaxed">{message.content}</div>
                                )}
                                <div
                                  className={`text-xs mt-3 ${
                                    message.role === 'user' ? 'text-black/60' : 'text-gray-500'
                                  }`}
                                >
                                  {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              </div>
                            </div>
                          ))}
                          <div ref={messagesEndRef} />
                        </div>
                      )}
                    </CardContent>

                    {/* Upload processing indicator */}
                    {uploadingFile && (
                      <div className="px-6 py-2 bg-[#fffdf0] border-t border-ugmonk-border flex items-center gap-2 text-sm text-ugmonk-dark">
                        <Loader className="w-4 h-4 animate-spin" />
                        Processing file... Extracting content
                      </div>
                    )}

                    {/* Input Area */}
                    <div className="px-6 py-5 border-t border-gray-200 bg-white flex-shrink-0">
                      <div className="flex items-center gap-3">
                        <input
                          ref={fileInputRef}
                          type="file"
                          onChange={handleFileUpload}
                          className="hidden"
                          accept=".pdf,.txt,.doc,.docx,.png,.jpg,.jpeg"
                        />
                        <Button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingFile}
                          variant="outline"
                          className="flex-shrink-0 h-12 w-12 p-0 text-gray-700 border border-gray-300 hover:border-[#ffd87a] hover:bg-[#fffdf0] transition-all rounded-lg"
                          title="Attach file"
                        >
                          {uploadingFile ? (
                            <Loader className="w-5 h-5 animate-spin text-ugmonk-dark" />
                          ) : (
                            <Paperclip className="w-5 h-5" />
                          )}
                        </Button>
                        <Button
                          onClick={() => setBulkUploadOpen(true)}
                          variant="outline"
                          className="flex-shrink-0 h-12 w-12 p-0 text-gray-700 border border-gray-300 hover:border-[#ffd87a] hover:bg-[#fffdf0] transition-all rounded-lg"
                          title="Bulk upload (up to 100 files)"
                        >
                          <Upload className="w-5 h-5" />
                        </Button>
                        <div className="flex-1 relative">
                          <textarea
                            ref={textareaRef}
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                sendMessage();
                              }
                            }}
                            placeholder="Type your message... (Shift+Enter for new line)"
                            className="w-full px-5 py-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#ffd87a] focus:border-[#ffd87a] bg-white transition-all text-gray-900"
                            rows={1}
                            style={{ maxHeight: '150px', minHeight: '48px' }}
                          />
                        </div>
                        <Button
                          onClick={sendMessage}
                          disabled={!inputMessage.trim() || sending}
                          className="flex-shrink-0 h-12 w-12 p-0 bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md transition-all rounded-lg"
                          title="Send message"
                        >
                          {sending ? (
                            <Loader className="w-5 h-5 animate-spin" />
                          ) : (
                            <Send className="w-5 h-5" />
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mt-3 text-center">
                        AI can make mistakes. Please verify important information.
                      </p>
                    </div>
                  </>
                )}
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Files Sidebar */}
      <Sheet open={filePreviewOpen} onOpenChange={setFilePreviewOpen}>
        <SheetContent side="right" className="w-[340px] sm:max-w-[380px] overflow-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <File className="w-5 h-5 text-ugmonk-dark" />
              Files ({files.length})
            </SheetTitle>
            <SheetDescription>Click a file to preview</SheetDescription>
          </SheetHeader>

          <div className="mt-4 space-y-2">
            {files.length === 0 ? (
              <p className="text-gray-500 text-center py-10">No files uploaded yet</p>
            ) : (
              files.map(file => (
                <div
                  key={file.id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-[#ffd87a] hover:bg-[#fffdf0]/50 transition-all cursor-pointer"
                  onClick={() => openFilePreview(file.id, file.filename)}
                >
                  <div className="w-10 h-10 rounded-lg bg-[#fffdf0] flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-ugmonk-dark" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{file.filename}</p>
                    <p className="text-xs text-gray-500">{file.file_type || 'Unknown type'}</p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteFile(file.id); }}
                    className="p-1.5 hover:bg-red-100 rounded-md transition-colors flex-shrink-0"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* File Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={(open) => {
        setPreviewDialogOpen(open);
        if (!open) setFilePreviewData(null);
      }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-ugmonk-dark" />
              {filePreviewData?.filename || 'File Preview'}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-auto">
            {(loadingPreview || !filePreviewData) ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <Loader className="w-8 h-8 animate-spin text-ugmonk-dark" />
                <p className="text-sm text-gray-500">Loading preview... This may take a moment for large files</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* PDF Preview */}
                {filePreviewData.file_type?.includes('pdf') && (
                  <div className="border rounded-lg overflow-hidden bg-gray-100 relative">
                    <iframe
                      src={`${API}/chat/sessions/${currentSession?.id}/files/${filePreviewData.fileId}/raw`}
                      className="w-full h-[600px]"
                      title="PDF Preview"
                      onError={() => {}}
                    />
                  </div>
                )}

                {/* Image Preview */}
                {filePreviewData.file_type?.includes('image') && (
                  <div className="flex justify-center bg-gray-100 rounded-lg p-4">
                    <img
                      src={`${API}/chat/sessions/${currentSession?.id}/files/${filePreviewData.fileId}/raw`}
                      alt={filePreviewData.filename}
                      className="max-w-full max-h-[600px] object-contain"
                    />
                  </div>
                )}

                {/* Fallback for unsupported types */}
                {!filePreviewData.file_type?.includes('pdf') && !filePreviewData.file_type?.includes('image') && (
                  <p className="text-gray-500 text-center py-10">Preview not available for this file type</p>
                )}

                {/* Metadata */}
                <div className="text-xs text-gray-500 pt-2 border-t">
                  <p>File type: {filePreviewData.file_type}</p>
                  <p>Uploaded: {new Date(filePreviewData.uploaded_at).toLocaleString()}</p>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        sessionId={currentSession?.id}
        onComplete={() => {
          if (currentSession) loadSession(currentSession.id);
        }}
      />

      {/* SharePoint Modal */}
      <SharePointModal
        open={sharePointOpen}
        onClose={() => setSharePointOpen(false)}
        sessionId={currentSession?.id}
        onSyncComplete={() => {
          if (currentSession) loadSession(currentSession.id);
        }}
      />

    </Layout>
  );
};

export default Chat;
